/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/*
 * The page modules, and the order they run in.
 *
 * Each was a script that registered itself on `DOMContentLoaded`, so the
 * order was whatever order the template happened to list them in. They
 * export an `init` now and the sequence is stated here instead.
 */

import { getConfig } from '../lib/config.js';

import { init as initBoot } from './boot.js';
import { init as initCookieConsent } from './cookie-consent-init.js';
import { init as initEditorialPanel } from './editorial-init.js';
import { init as initGlossary } from './glossary-init.js';
import { init as initLangSwitch } from './lang-switch.js';
import { layout } from '../lib/dom.js';
import { init as initHeader } from './header.js';
import { init as initMap } from './map-init.js';
import { init as initMeteredLightbox } from './metered-lightbox.js';
import { init as initPageSetup } from './page-setup.js';
import { init as initPreviewPoller } from './preview-poller.js';
import { init as initSearch } from './search.js';
import { init as initShare } from './share.js';
import { init as initStats } from './stats.js';
import { init as initToc } from './toc.js';
import { init as initTouchGuard } from './touch-guard.js';

export {
    initBoot,
    initCookieConsent,
    initEditorialPanel,
    initGlossary,
    initLangSwitch,
    initMap,
    initMeteredLightbox,
    initPageSetup,
    initPreviewPoller,
    initSearch,
    initShare,
    initStats,
    initToc,
    initTouchGuard,
};

/**
 * Start the page modules this map actually needs.
 *
 * The order is a real constraint, not a preference. Boot runs first
 * because it reads the configuration everything else depends on. The shell
 * is trimmed next, so nothing wires itself to chrome that is about to be
 * removed. Consent follows: the content preprocessors ask it what may
 * load, and a module rendering content before the answer is known would
 * fetch third-party media the reader has not agreed to. The map goes last,
 * because it is the thing every other module attaches to.
 *
 * Which modules run is a constraint too. The template used to decide by
 * emitting a `<script>` tag or not, so a map without search never loaded
 * the search module and a published map never loaded the preview poller.
 * A bundle has them all, so the condition has to be stated here — without
 * it, every visitor to every published map polls for preview updates
 * forever.
 *
 * @returns {Promise<void>} Resolves once the map has been built.
 */
export function initAll() {
    initBoot();
    const config = getConfig();

    initPageSetup();
    initCookieConsent();
    initTouchGuard();
    initShare();

    if (config.map?.showToc) initToc();
    if (config.map?.showSearch) initSearch();
    if (config.client?.hasI18n) initLangSwitch();
    if (config.map?.showEditorial) initEditorialPanel();
    if (config.metering?.enabledForViewer) initMeteredLightbox();
    if (config.preview?.enabled) initPreviewPoller();

    initHeader();
    // After the header, and only after: it measures the bar and reserves
    // that much room at the top of the map, so the point panel starts
    // below it. Measured while the bar is still hidden it reads zero, and
    // the panel opens underneath the header with its heading covered.
    layout();
    initGlossary();
    initStats();

    return initMap();
}
